/**
 * CompetitionLogin.jsx — Judge login page. Two modes:
 *   1. Personal judge code (single-use, per-name)
 *   2. Shared password fallback (issued to Stockaris internally)
 * Both land the caller on the isolated demo tenant.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Trophy, ArrowRight, ShieldCheck, Ticket, LockKey } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import {
  bootstrapCompetitionDemo,
  trackCompetitionEvent,
  redeemJudgeCode,
} from '../lib/api';

const DEFAULT_EMAIL = 'judge@stockaris.demo';

export default function CompetitionLogin() {
  const navigate = useNavigate();
  const { login, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState('code'); // 'code' | 'password'
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [email, setEmail] = useState(DEFAULT_EMAIL);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const bootstrapRef = useRef(null);
  useEffect(() => {
    bootstrapRef.current = bootstrapCompetitionDemo().catch(() => null);
  }, []);

  // If code is passed via URL (?code=XXXX-XXXX-XXXX), auto-redeem.
  const autoRedeemed = useRef(false);
  useEffect(() => {
    const c = (searchParams.get('code') || '').trim();
    if (c && !autoRedeemed.current) {
      autoRedeemed.current = true;
      // small delay so the bootstrap request has time to fire
      setTimeout(() => submitCode(c), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const afterLoginSuccess = async (label) => {
    trackCompetitionEvent('demo.judge_logged_in', null, { via: label }).catch(() => {});
    navigate('/competition-demo/dashboard');
  };

  const submitPassword = async (e) => {
    e?.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      try {
        if (!bootstrapRef.current) {
          bootstrapRef.current = bootstrapCompetitionDemo().catch(() => null);
        }
        await bootstrapRef.current;
      } catch (_) {}
      const res = await login(email, password);
      if (res?.success) {
        await afterLoginSuccess('password');
      } else {
        toast.error(res?.error || 'Login failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (override) => {
    if (busy) return;
    const value = ((override ?? code) || '').trim();
    if (value.length < 6) {
      toast.error('Enter a valid judge code');
      return;
    }
    setBusy(true);
    try {
      try {
        if (!bootstrapRef.current) {
          bootstrapRef.current = bootstrapCompetitionDemo().catch(() => null);
        }
        await bootstrapRef.current;
      } catch (_) {}
      await redeemJudgeCode(value);
      // The redeem endpoint already set the auth cookie; hydrate the user.
      await checkAuth();
      await afterLoginSuccess('code');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) toast.error(detail || 'Code not found');
      else if (status === 409) toast.error(detail || 'Code already used — ask for a fresh one');
      else if (status === 410) toast.error(detail || 'Code expired or revoked');
      else toast.error(detail || 'Redemption failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center p-6" data-testid="competition-login">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.4em] text-emerald-400 mb-6">
          <Trophy size={16} weight="duotone" />
          Judge · Competition Access
        </div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">Enter the demo</h1>
        <p className="text-neutral-400 mb-6 text-sm">
          You will land on an isolated tenant. Nothing you do here can touch production data.
        </p>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 mb-4 border border-neutral-800 rounded-none overflow-hidden text-xs uppercase tracking-widest">
          <button
            type="button"
            onClick={() => setMode('code')}
            className={`py-3 flex items-center justify-center gap-2 font-black transition-colors ${
              mode === 'code' ? 'bg-emerald-500 text-neutral-950' : 'bg-neutral-900 text-neutral-400 hover:text-neutral-200'
            }`}
            data-testid="cd-mode-code"
          >
            <Ticket size={14} weight="fill" /> Judge Code
          </button>
          <button
            type="button"
            onClick={() => setMode('password')}
            className={`py-3 flex items-center justify-center gap-2 font-black transition-colors ${
              mode === 'password' ? 'bg-emerald-500 text-neutral-950' : 'bg-neutral-900 text-neutral-400 hover:text-neutral-200'
            }`}
            data-testid="cd-mode-password"
          >
            <LockKey size={14} weight="fill" /> Shared password
          </button>
        </div>

        {mode === 'code' ? (
          <form
            onSubmit={(e) => { e.preventDefault(); submitCode(); }}
            className="space-y-4 bg-neutral-900 border border-neutral-800 p-6"
          >
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Personal judge code</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABCD-EFGH-JKLM"
                className="rounded-none bg-neutral-950 border-neutral-800 text-neutral-100 font-mono tracking-widest"
                autoFocus
                autoComplete="off"
                data-testid="cd-code-input"
              />
              <p className="text-[11px] text-neutral-500 pt-1">
                Codes are single-use and tied to your name. Ask the Stockaris team for one.
              </p>
            </div>
            <Button
              type="submit"
              disabled={busy || code.trim().length < 6}
              className="w-full rounded-none bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-black uppercase tracking-widest text-xs py-6 disabled:opacity-50"
              data-testid="cd-code-submit"
            >
              {busy ? 'Redeeming…' : (<>Redeem &amp; enter <ArrowRight size={16} className="ml-2" /></>)}
            </Button>
            <div className="flex items-center gap-2 text-[10px] text-neutral-500 uppercase tracking-widest pt-2 border-t border-neutral-800">
              <ShieldCheck size={12} className="text-emerald-500" /> Isolated tenant · Revokable at any time
            </div>
          </form>
        ) : (
          <form onSubmit={submitPassword} className="space-y-4 bg-neutral-900 border border-neutral-800 p-6">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Email</Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none bg-neutral-950 border-neutral-800 text-neutral-100"
                data-testid="cd-login-email"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Shared password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none bg-neutral-950 border-neutral-800 text-neutral-100"
                placeholder="Provided by the Stockaris team"
                autoFocus
                autoComplete="off"
                data-testid="cd-login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={busy || !password}
              className="w-full rounded-none bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-black uppercase tracking-widest text-xs py-6 disabled:opacity-50"
              data-testid="cd-login-submit"
            >
              {busy ? 'Entering…' : (<>Enter the challenge <ArrowRight size={16} className="ml-2" /></>)}
            </Button>
            <div className="flex items-center gap-2 text-[10px] text-neutral-500 uppercase tracking-widest pt-2 border-t border-neutral-800">
              <ShieldCheck size={12} className="text-emerald-500" /> Isolated tenant · Reset any time
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
